const API_URL = import.meta.env.VITE_API_URL || '/api'

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  token?: string
}

export type AppLocale = 'en' | 'he'

class ApiClient {
  private baseUrl: string
  private token: string | null = null
  private locale: AppLocale = 'en'

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  setToken(token: string | null) {
    this.token = token
  }

  setLocale(locale: AppLocale) {
    this.locale = locale
  }

  getLocale() {
    return this.locale
  }

  private async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, token } = options

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Locale': this.locale,
      'Accept-Language': this.locale,
    }

    const authToken = token || this.token
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const config: RequestInit = {
      method,
      headers,
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config)

    if (!response.ok) {
      const errorText = await response.text()
      let errorDetail = 'Request failed'
      try {
        const errorJson = JSON.parse(errorText)
        errorDetail = errorJson.detail || errorDetail
      } catch {
        errorDetail = errorText || errorDetail
      }
      throw new Error(errorDetail)
    }

    return response.json()
  }

  // ============== Auth ==============

  async signUp(email: string, password: string, businessName?: string, fullName?: string) {
    return this.request('/auth/signup', {
      method: 'POST',
      body: { email, password, business_name: businessName, full_name: fullName }
    })
  }

  async signIn(email: string, password: string) {
    return this.request<{
      success: boolean
      access_token?: string
      refresh_token?: string
      user?: {
        id: string
        email: string
        business_name?: string
      }
    }>('/auth/signin', {
      method: 'POST',
      body: { email, password }
    })
  }

  async signOut() {
    return this.request('/auth/signout', { method: 'POST' })
  }

  async refreshToken(refreshToken: string) {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: { refresh_token: refreshToken }
    })
  }

  async getMe() {
    return this.request('/auth/me')
  }

  async updateProfile(data: Record<string, unknown>) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: data
    })
  }

  // ============== Proposals ==============

  async getProposals(status?: string) {
    const params = status ? `?status=${status}` : ''
    return this.request<{ proposals: unknown[], total: number }>(`/proposals${params}`)
  }

  async getProposal(id: string) {
    return this.request(`/proposals/${id}`)
  }

  async createProposal(data: Record<string, unknown>) {
    return this.request('/proposals', {
      method: 'POST',
      body: data
    })
  }

  async updateProposal(id: string, data: Record<string, unknown>) {
    return this.request(`/proposals/${id}`, {
      method: 'PUT',
      body: data
    })
  }

  async deleteProposal(id: string) {
    return this.request(`/proposals/${id}`, { method: 'DELETE' })
  }

  async updateProposalStatus(id: string, status: string) {
    return this.request(`/proposals/${id}/status?status=${status}`, { method: 'PATCH' })
  }

  async sendProposal(id: string, personalMessage?: string) {
    return this.request(`/proposals/${id}/send`, {
      method: 'POST',
      body: { personal_message: personalMessage }
    })
  }

  async sendReminder(id: string, message?: string) {
    return this.request<{ success: boolean; message?: string }>(`/proposals/${id}/remind`, {
      method: 'POST',
      body: { message }
    })
  }

  async getProposalAnalytics(id: string) {
    return this.request<ProposalAnalytics>(`/proposals/${id}/analytics`)
  }

  // ============== View Tracking ==============

  async trackProposalView(proposalId: string) {
    return this.request<{ success: boolean; view_id: string; device_type: string }>(
      `/proposals/${proposalId}/track-view`,
      { method: 'POST' }
    )
  }

  async updateViewDuration(proposalId: string, viewId: string, durationSeconds: number) {
    return this.request<{ success: boolean; view_id: string; duration_seconds: number }>(
      `/proposals/${proposalId}/track-view/${viewId}`,
      { method: 'PUT', body: { duration_seconds: durationSeconds } }
    )
  }

  // ============== Public Viewing ==============

  async viewProposal(shareToken: string) {
    return this.request<{ proposal: Record<string, unknown> }>(`/view/${shareToken}`)
  }

  async signProposal(shareToken: string, signerName: string, signerEmail: string, signatureData: string) {
    return this.request(`/view/${shareToken}/sign`, {
      method: 'POST',
      body: {
        signer_name: signerName,
        signer_email: signerEmail,
        signature_data: signatureData
      }
    })
  }

  // ============== PDF Generation ==============

  async generateProposal(data: Record<string, unknown>) {
    return this.request<{ pdf_base64: string, html_preview: string }>('/generate-proposal', {
      method: 'POST',
      body: data
    })
  }

  async previewProposal(data: Record<string, unknown>) {
    const response = await fetch(`${this.baseUrl}/preview-proposal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Locale': this.locale,
        'Accept-Language': this.locale,
      },
      body: JSON.stringify(data)
    })
    return response.text()
  }

  // ============== Clients (CRM) ==============

  async getClients(status?: string, search?: string) {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    if (search) params.append('search', search)
    const queryString = params.toString()
    return this.request<{ clients: Client[], total: number }>(`/clients${queryString ? `?${queryString}` : ''}`)
  }

  async getClient(id: string) {
    return this.request<Client>(`/clients/${id}`)
  }

  async createClient(data: Partial<Client>) {
    return this.request<{ success: boolean, client: Client }>('/clients', {
      method: 'POST',
      body: data
    })
  }

  async updateClient(id: string, data: Partial<Client>) {
    return this.request<{ success: boolean, client: Client }>(`/clients/${id}`, {
      method: 'PUT',
      body: data
    })
  }

  async deleteClient(id: string) {
    return this.request(`/clients/${id}`, { method: 'DELETE' })
  }

  async getClientProposals(id: string) {
    return this.request<{ proposals: unknown[], total: number }>(`/clients/${id}/proposals`)
  }

  async refreshClientStats(id: string) {
    return this.request<{ success: boolean, client: Client }>(`/clients/${id}/refresh-stats`, {
      method: 'POST'
    })
  }

  // ============== AI Services ==============

  async analyzeRisks(proposalContent: string, pricingItems: unknown[] = []) {
    return this.request<AIAnalysisResult>('/ai/analyze-risks', {
      method: 'POST',
      body: {
        proposal_content: proposalContent,
        pricing_items: pricingItems,
        language: this.locale
      }
    })
  }

  async improveText(text: string, action: 'improve' | 'shorten' | 'expand' | 'professional' | 'friendly' = 'improve') {
    return this.request<AITextResult>('/ai/improve-text', {
      method: 'POST',
      body: { text, action, language: this.locale }
    })
  }

  async generateContent(contentType: 'intro' | 'benefits' | 'terms' | 'timeline' | 'faq', context: string) {
    return this.request<AIContentResult>('/ai/generate-content', {
      method: 'POST',
      body: { content_type: contentType, context, language: this.locale }
    })
  }

  async generateProposalAI(prompt: string, clientName?: string, clientCompany?: string, industry?: string) {
    return this.request<AIProposalResult>('/ai/generate-proposal', {
      method: 'POST',
      body: {
        prompt,
        client_name: clientName,
        client_company: clientCompany,
        industry,
        language: this.locale
      }
    })
  }

  async suggestPrice(serviceDescription: string, clientIndustry?: string, projectSize: string = 'medium') {
    return this.request<AIPriceResult>('/ai/suggest-price', {
      method: 'POST',
      body: {
        service_description: serviceDescription,
        client_industry: clientIndustry,
        project_size: projectSize
      }
    })
  }

  // ============== Reminders ==============

  async getReminderSettings() {
    return this.request<{ success: boolean; settings: ReminderSettings }>('/reminders/settings')
  }

  async updateReminderSettings(settings: Partial<ReminderSettings>) {
    return this.request<{ success: boolean; settings: ReminderSettings }>('/reminders/settings', {
      method: 'PUT',
      body: settings
    })
  }

  async processReminders() {
    return this.request<{
      success: boolean
      sent: number
      failed: number
      total_eligible: number
      results: ReminderResult[]
    }>('/reminders/process', {
      method: 'POST'
    })
  }

  // ============== Settings ==============

  async getSettings() {
    return this.request<{ success: boolean; settings: Record<string, unknown> }>('/settings')
  }

  async saveSettings(settings: Record<string, unknown>) {
    return this.request<{ success: boolean; settings: Record<string, unknown> }>('/settings', {
      method: 'PUT',
      body: settings
    })
  }

  // ============== Brand Kit ==============

  async getBrandKit() {
    return this.request<{ success: boolean; brand_kit: BrandKit }>('/brand-kit')
  }

  async updateBrandKit(data: BrandKit) {
    return this.request<{ success: boolean; brand_kit: BrandKit }>('/brand-kit', {
      method: 'PUT',
      body: data
    })
  }

  // ============== Admin ==============

  async checkAdmin() {
    return this.request<{ is_admin: boolean }>('/admin/check')
  }

  async getAdminStats() {
    return this.request<AdminStats>('/admin/stats')
  }

  async getAdminUsers() {
    return this.request<{ users: AdminUser[]; total: number }>('/admin/users')
  }

  async getAdminPayments() {
    return this.request<{ payments: AdminPayment[]; total: number }>('/admin/payments')
  }

  async updateUserPlan(userId: string, plan: 'free' | 'pro' | 'business') {
    return this.request<{ success: boolean; user_id: string; plan: string }>(
      `/admin/users/${userId}/plan`,
      { method: 'POST', body: { plan } }
    )
  }

  async suspendUser(userId: string, suspend: boolean) {
    return this.request<{ success: boolean; user_id: string; suspended: boolean }>(
      `/admin/users/${userId}/suspend`,
      { method: 'POST', body: { suspend } }
    )
  }

  async getAdminProposals(params?: {
    status?: string
    user_id?: string
    date_from?: string
    date_to?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    const sp = new URLSearchParams()
    if (params?.status) sp.set('status', params.status)
    if (params?.user_id) sp.set('user_id', params.user_id)
    if (params?.date_from) sp.set('date_from', params.date_from)
    if (params?.date_to) sp.set('date_to', params.date_to)
    if (params?.search) sp.set('search', params.search)
    if (params?.limit) sp.set('limit', String(params.limit))
    if (params?.offset) sp.set('offset', String(params.offset))
    const q = sp.toString() ? `?${sp.toString()}` : ''
    return this.request<{ proposals: AdminProposal[]; total: number }>(`/admin/proposals${q}`)
  }

  async getAdminActivity(params?: { type?: string; limit?: number; offset?: number; since?: string }) {
    const sp = new URLSearchParams()
    if (params?.type) sp.set('type', params.type)
    if (params?.limit) sp.set('limit', String(params.limit))
    if (params?.offset) sp.set('offset', String(params.offset))
    if (params?.since) sp.set('since', params.since)
    const q = sp.toString() ? `?${sp.toString()}` : ''
    return this.request<{ events: AdminActivityEvent[]; total: number }>(`/admin/activity${q}`)
  }

  async getTestMode() {
    return this.request<{ enabled: boolean; test_plans_configured: boolean }>('/admin/test-mode')
  }

  async setTestMode(enabled: boolean) {
    return this.request<{ success: boolean; enabled: boolean }>('/admin/test-mode', {
      method: 'PUT',
      body: { enabled },
    })
  }

  async simulateSubscription(plan: string, userId?: string) {
    return this.request<{ success: boolean; plan: string; email_sent: boolean; email_details: string; message: string }>('/admin/simulate-subscription', {
      method: 'POST',
      body: { plan, user_id: userId },
    })
  }

  async broadcastNotification(title: string, content: string) {
    return this.request<{ success: boolean; sent_to: number }>('/admin/notifications/broadcast', {
      method: 'POST',
      body: { title, content },
    })
  }

  async getNotifications(limit?: number, unreadOnly?: boolean) {
    const sp = new URLSearchParams()
    if (limit) sp.set('limit', String(limit))
    if (unreadOnly) sp.set('unread_only', 'true')
    const q = sp.toString() ? `?${sp.toString()}` : ''
    return this.request<{ notifications: UserNotification[]; total: number }>(`/notifications${q}`)
  }

  async markNotificationRead(notificationId: string) {
    return this.request<{ success: boolean }>(`/notifications/${notificationId}/read`, { method: 'PUT' })
  }

  async markAllNotificationsRead() {
    return this.request<{ success: boolean }>('/notifications/read-all', { method: 'PUT' })
  }

  async getUnreadNotificationCount() {
    return this.request<{ count: number }>('/notifications/unread-count')
  }

  async exportAdminData(type: 'users' | 'payments' | 'proposals' | 'stats', format: 'csv' | 'json' = 'csv') {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`
    const res = await fetch(`${this.baseUrl}/admin/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, format }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Export failed' }))
      throw new Error(err.detail || 'Export failed')
    }
    if (format === 'csv' || res.headers.get('content-type')?.includes('text/csv')) {
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `admin_export_${type}_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
      return
    }
    return res.json() as Promise<{ data: unknown[]; headers: string[] }>
  }

  // ============== Subscription ==============

  async getSubscription() {
    return this.request<SubscriptionResponse>('/subscription')
  }

  async getPayments() {
    return this.request<{ payments: PaymentRecord[] }>('/subscription/payments')
  }

  async getPayPalConfig() {
    return this.request<PayPalConfig>('/paypal/config')
  }

  async createSubscription(plan: string, returnUrl: string, cancelUrl: string) {
    return this.request<{ subscription_id: string; approval_url: string; status: string }>('/subscription/create', {
      method: 'POST',
      body: { plan, return_url: returnUrl, cancel_url: cancelUrl }
    })
  }

  async activateSubscription(subscriptionId: string) {
    return this.request<{ success: boolean; plan: string; subscription_id: string }>('/subscription/activate', {
      method: 'POST',
      body: { subscription_id: subscriptionId }
    })
  }

  async cancelSubscription() {
    return this.request<{ success: boolean; plan: string }>('/subscription/cancel', {
      method: 'POST'
    })
  }

  async deleteAccount() {
    return this.request<{ success: boolean; message: string }>('/account', {
      method: 'DELETE'
    })
  }

  // ============== Audit Trail ==============

  async getAuditTrail(proposalId: string) {
    return this.request<{ success: boolean; proposal_id: string; events: AuditEvent[] }>(
      `/proposals/${proposalId}/audit-trail`
    )
  }

  async getAuditCertificate(proposalId: string) {
    return this.request<{ success: boolean; certificate: AuditCertificate }>(
      `/proposals/${proposalId}/audit-certificate`
    )
  }

  // ============== Comments/Feedback ==============

  async getProposalComments(proposalId: string) {
    return this.request<{ success: boolean; comments: ProposalComment[] }>(
      `/proposals/${proposalId}/comments`
    )
  }

  async addProposalComment(proposalId: string, data: {
    author_name: string
    content: string
    author_email?: string
    is_owner?: boolean
  }) {
    return this.request<{ success: boolean; comment: ProposalComment }>(
      `/proposals/${proposalId}/comments`,
      { method: 'POST', body: data }
    )
  }

  async markCommentRead(proposalId: string, commentId: string) {
    return this.request<{ success: boolean; comment: ProposalComment }>(
      `/proposals/${proposalId}/comments/${commentId}/read`,
      { method: 'PUT' }
    )
  }

  async getUnreadCommentCounts() {
    return this.request<{ success: boolean; unread_counts: Record<string, number> }>(
      '/proposals/comments/unread'
    )
  }

  // ============== Attachments ==============

  // ============== Dashboard Analytics ==============

  async getDashboardAnalytics() {
    return this.request<DashboardAnalyticsData>('/analytics/dashboard')
  }

  async exportUserData(entities?: Array<'profile' | 'proposals' | 'clients' | 'templates' | 'comments' | 'audit'>) {
    return this.request<ExportDataResponse>('/export/data', {
      method: 'POST',
      body: { entities }
    })
  }

  async uploadAttachment(file: File): Promise<{ success: boolean; url?: string; path?: string; file_name?: string; size?: number; error?: string }> {
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const response = await fetch(`${this.baseUrl}/upload-attachment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': this.locale,
          'Accept-Language': this.locale,
          ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {})
        },
        body: JSON.stringify({
          file_data: base64,
          file_name: file.name,
          content_type: file.type
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        return { success: false, error: errorData.detail || 'Upload failed' }
      }

      return await response.json()
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Upload failed'
      }
    }
  }

  // ============== Webhooks ==============

  async getWebhooks() {
    return this.request<{ success: boolean; webhooks: Webhook[] }>('/webhooks')
  }

  async createWebhook(data: { url: string; events: string[]; secret: string }) {
    return this.request<{ success: boolean; webhook: Webhook }>('/webhooks', {
      method: 'POST',
      body: data,
    })
  }

  async deleteWebhook(id: string) {
    return this.request<{ success: boolean; message: string }>(`/webhooks/${id}`, {
      method: 'DELETE',
    })
  }

  async testWebhook(id: string) {
    return this.request<{ success: boolean; status_code?: number; message?: string; error?: string }>(
      `/webhooks/${id}/test`,
      { method: 'POST' }
    )
  }
}

// Webhook type
export interface Webhook {
  id: string
  user_id: string
  url: string
  events: string[]
  secret: string
  active: boolean
  created_at: string
  updated_at: string
  last_delivery_status: string | null
  last_delivery_at: string | null
}

// Analytics Types
export interface ProposalAnalytics {
  proposal_id: string
  total_views: number
  unique_views: number
  avg_duration: number
  views_by_device: Record<string, number>
  views_timeline: { date: string; count: number }[]
  recent_views: {
    id: string
    viewed_at: string
    duration_seconds: number
    device_type: string
    referrer?: string
  }[]
  status: string
  sent_at?: string
  viewed_at?: string
  signed_at?: string
  events: unknown[]
}

// Audit Trail Types
export interface AuditEvent {
  id: string
  proposal_id: string
  event_type: string
  signer_name: string
  signer_email: string
  ip_address: string | null
  user_agent: string | null
  document_hash: string
  signature_data_hash: string
  timestamp: string
  created_at: string
}

export interface AuditCertificate {
  certificate_id: string
  generated_at: string
  document: {
    proposal_id: string
    proposal_number: string
    title: string
    status: string
    created_at: string
    total_price: number | null
    currency: string
  }
  signer: {
    name: string
    email: string
    signed_at: string
    ip_address: string
  }
  verification: {
    document_hash: string
    signature_hash: string
    is_verified: boolean
  }
  events: {
    event_type: string
    timestamp: string
    signer_name: string
    signer_email: string
    ip_address: string
    user_agent: string
    document_hash: string
    signature_data_hash: string
  }[]
}

// AI Response Types
export interface AIAnalysisResult {
  success: boolean
  risks: string[]
  missing_clauses: string[]
  score: number
  recommendations: string[]
}

export interface AITextResult {
  success: boolean
  original: string
  improved: string
  action: string
  error?: string
}

export interface AIContentResult {
  success: boolean
  content_type: string
  content?: string
  items?: unknown[]
  error?: string
}

export interface AIProposalResult {
  success: boolean
  blocks: unknown[]
  pricing_items: unknown[]
  total_price: number
  title: string
  valid_days: number
  is_fallback?: boolean
}

export interface AIPriceResult {
  success?: boolean
  suggested_price: number | null
  min_price?: number
  max_price?: number
  confidence: number
  source: string
  reasoning?: string
  message?: string
}

// Client type
export interface Client {
  id: string
  user_id: string
  name: string
  email?: string
  phone?: string
  company?: string
  address?: string
  city?: string
  country?: string
  website?: string
  tax_id?: string
  contact_person?: string
  notes?: string
  tags?: string[]
  status: 'active' | 'inactive' | 'lead' | 'vip'
  total_proposals: number
  total_signed: number
  total_value: number
  last_proposal_at?: string
  created_at: string
  updated_at: string
}

// Subscription types
export interface SubscriptionResponse {
  subscription: {
    plan: 'free' | 'pro' | 'business'
    status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'expired'
    paypal_subscription_id?: string
    current_period_start?: string
    current_period_end?: string
    price_amount: number
    price_currency: string
    cancelled_at?: string
  }
  limits: {
    max_proposals: number
    ai_enabled: boolean
    pdf_enabled: boolean
    templates_enabled: boolean
    email_notifications: boolean
  }
  usage: {
    proposals_count: number
    proposals_limit: number
    proposals_remaining: number
  }
}

export interface PaymentRecord {
  id: string
  amount: number
  currency: string
  status: string
  paypal_payment_id?: string
  invoice_number?: string
  description?: string
  created_at: string
}

export interface PayPalConfig {
  client_id: string
  plan_ids: { pro: string; business: string }
  mode: 'sandbox' | 'live'
  configured: boolean
}

// Admin types
export interface AdminStats {
  total_users: number
  pro_users: number
  free_users: number
  total_proposals: number
  signed_proposals: number
  total_revenue: number
  total_proposals_value: number
  sent_proposals?: number
  viewed_proposals?: number
  users_this_month?: number
  users_last_month?: number
  revenue_this_month?: number
  revenue_last_month?: number
  proposals_this_month?: number
  proposals_last_month?: number
  signed_this_month?: number
  signed_last_month?: number
  active_subscriptions?: number
  cancelled_this_month?: number
  total_views?: number
  total_signed_events?: number
  views_by_type?: Record<string, number>
  monthly?: { month: string; created: number; sent: number; viewed: number; signed: number; revenue: number }[]
}

export interface AdminUser {
  id: string
  email: string
  full_name: string | null
  business_name: string | null
  avatar_url?: string | null
  plan: 'free' | 'pro' | 'business'
  proposals_count: number
  signed_count: number
  proposals_value: number
  created_at: string
  is_suspended?: boolean
}

export interface AdminPayment {
  id: string
  user_id: string
  amount: number
  currency: string
  status: string
  paypal_payment_id?: string
  description?: string
  created_at: string
  user_email: string
  user_name: string
}

export interface AdminProposal {
  id: string
  proposal_number: string
  user_id: string
  user_email: string
  client_name: string
  status: string
  total_price: number
  created_at: string
  signed_at: string | null
  share_token: string
}

export interface UserNotification {
  id: string
  title: string
  content: string
  is_read: boolean
  created_at: string
}

export interface AdminActivityEvent {
  id: string
  type: 'signature' | 'payment' | 'view' | 'signed'
  timestamp: string
  proposal_id: string | null
  proposal_number?: string
  client_name?: string
  share_token?: string
  user_id: string | null
  user_email: string
  description: string
  amount?: number
}

// Reminder types
export interface ReminderSettings {
  auto_reminders_enabled: boolean
  reminder_days: number[]
  max_reminders: number
  reminder_message: string
}

export interface ReminderResult {
  proposal_id: string
  proposal_title?: string
  client_email?: string
  success: boolean
  error?: string
}

// Proposal Comment type
export interface ProposalComment {
  id: string
  proposal_id: string
  author_name: string
  author_email?: string
  content: string
  is_owner: boolean
  is_read: boolean
  user_id?: string
  created_at: string
}

// Dashboard Analytics type
export interface DashboardAnalyticsData {
  totals: {
    created: number
    sent: number
    viewed: number
    signed: number
    revenue: number
  }
  monthly: {
    month: string
    sent: number
    viewed: number
    signed: number
    revenue: number
  }[]
  conversion_rate: number
  avg_time_to_sign: number
  current_month?: {
    created: number
    sent: number
    viewed: number
    signed: number
    revenue: number
  }
  previous_month?: {
    created: number
    sent: number
    viewed: number
    signed: number
    revenue: number
  }
}

// Brand Kit type
export interface BrandKit {
  primaryColor: string
  secondaryColor: string
  fontFamily: string
  logoUrl: string
}

export interface ExportDataResponse {
  success: boolean
  exported_at: string
  user_id: string
  entities: string[]
  summary: Record<string, number>
  data: Record<string, unknown>
}

export const api = new ApiClient(API_URL)
export default api
